import { useState } from 'react';
import { D, st, toArr } from '../constants';
import { Overlay, MHdr, Field, StatGrid } from './shared';

const CREDITOR_CATS = ["Supply House","Fuel","Hardware Store","Tool & Equipment","Permits & Fees","Subcontractor","Vehicle","Utilities","Credit Card","Other"];
const PRESET_VENDORS = {
  "Supply Houses": ["Graybar Electric","Raybro Electric","Wesco","Anixter","Border States Electric","Rexel","CED (City Electric Supply)","Mayer Electric","Eaton","Hubbell","Leviton","Legrand","Southwire","General Cable","Encore Wire","Ideal Industries"],
  "Hardware Stores": ["Home Depot","Lowe's","Ace Hardware","True Value","Menards","84 Lumber","Fastenal","Grainger","MSC Industrial"],
  "Fuel": ["Shell","Chevron","ExxonMobil","BP","Circle K","RaceTrac","Wawa","Sunoco","Marathon","Phillips 66","Love's Truck Stop","Pilot Flying J","Fleet One","WEX Fuel"],
  "Electrical Wholesale": ["Platt Electric","WESCO","Crescent Electric","Schaedler Yesco","Springfield Electric","Van Meter","Irby","Stuart C. Irby","Elliott Electric"],
  "Credit Cards": ["Chase Business Card","American Express Business","Capital One Spark","Bank of America Business","Wells Fargo Business","Citi Business Card","US Bank Business","Home Depot Credit","Lowes Business Credit","Fuel Card / Fleet Card"],
  "Other": ["Custom / Other"],
};
const PRESET_CATS = {
  "Supply Houses":"Supply House", "Hardware Stores":"Hardware Store", "Fuel":"Fuel",
  "Electrical Wholesale":"Supply House", "Credit Cards":"Credit Card", "Other":"Other",
};

export default function Creditors({creditors, setCreditors, fmt, fmtDate}) {
  const vendors = toArr(creditors.vendors);
  const receipts = toArr(creditors.receipts);
  const setVendors = fn => setCreditors(c => ({...c, vendors: fn(toArr(c.vendors))}));
  const setReceipts = fn => setCreditors(c => ({...c, receipts: fn(toArr(c.receipts))}));

  const today = new Date();
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [tab, setTab] = useState("monthly");
  const [vendorModal, setVendorModal] = useState(null);
  const [vData, setVData] = useState({});
  const setVD = (k,v) => setVData(x => ({...x, [k]: v}));
  const [receiptModal, setReceiptModal] = useState(null);
  const [rData, setRData] = useState({});
  const setRD = (k,v) => setRData(x => ({...x, [k]: v}));
  const [selectedVendor, setSelectedVendor] = useState(null);

  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const pad = n => String(n).padStart(2,"0");
  const monthStr = `${viewYear}-${pad(viewMonth+1)}`;
  const prevMonth = () => { if (viewMonth===0) { setViewMonth(11); setViewYear(y=>y-1); } else setViewMonth(m=>m-1); };
  const nextMonth = () => { if (viewMonth===11) { setViewMonth(0); setViewYear(y=>y+1); } else setViewMonth(m=>m+1); };

  const monthReceipts = receipts.filter(r => r.date && r.date.startsWith(monthStr));
  const allReceipts = selectedVendor ? receipts.filter(r => r.vendorId===selectedVendor) : monthReceipts;

  const vendorTotals = {};
  monthReceipts.forEach(r => { vendorTotals[r.vendorId] = (vendorTotals[r.vendorId]||0) + (Number(r.amount)||0); });
  const monthTotal = monthReceipts.reduce((s,r)=>s+(Number(r.amount)||0),0);
  const unpaidTotal = monthReceipts.filter(r=>r.status==="Unpaid").reduce((s,r)=>s+(Number(r.amount)||0),0);
  const paidTotal = monthReceipts.filter(r=>r.status==="Paid").reduce((s,r)=>s+(Number(r.amount)||0),0);

  const openAddVendor = () => { setVData({id:null,name:"",category:"Supply House",accountNum:"",phone:"",notes:"",isCC:false,ccLimit:"",ccDueDay:"",ccLastFour:"",presetGroup:"Supply Houses",presetName:""}); setVendorModal("add"); };
  const openEditVendor = v => { setVData({...v,presetGroup:"",presetName:""}); setVendorModal("edit"); };
  const saveVendor = () => {
    let name = vData.name||"";
    let cat = vData.category||"Supply House";
    if (vendorModal==="add" && vData.presetName && vData.presetName!=="Custom / Other") {
      name = vData.presetName;
      cat = PRESET_CATS[vData.presetGroup] || "Other";
      if (vData.presetGroup==="Credit Cards") cat = "Credit Card";
    }
    const rec = {...vData, name, category:cat, id: vData.id || (Math.max(0,...vendors.map(x=>x.id),0)+1)};
    setVendors(prev => vData.id ? prev.map(x=>x.id===rec.id?rec:x) : [...prev, rec]);
    setVendorModal(null);
  };
  const deleteVendor = id => { if (confirm('Delete this vendor?')) setVendors(prev => prev.filter(x=>x.id!==id)); };

  const openAddReceipt = (vendorId=null) => {
    const v = vendors.find(x=>x.id===vendorId) || vendors[0];
    setRData({id:null,vendorId:v?.id||null,date:new Date().toISOString().slice(0,10),amount:"",description:"",invoiceNum:"",status:"Unpaid",paidDate:null,notes:""});
    setReceiptModal("add");
  };
  const openEditReceipt = r => { setRData({...r}); setReceiptModal("edit"); };
  const saveReceipt = () => {
    const rec = {...rData, amount: parseFloat(rData.amount)||0, id: rData.id || (Math.max(0,...receipts.map(x=>x.id),0)+1)};
    setReceipts(prev => rData.id ? prev.map(x=>x.id===rec.id?rec:x) : [...prev, rec]);
    setReceiptModal(null);
  };
  const deleteReceipt = id => { if (confirm('Delete this receipt?')) setReceipts(prev => prev.filter(x=>x.id!==id)); };
  const markReceiptPaid = id => setReceipts(prev => prev.map(x=>x.id===id?{...x,status:"Paid",paidDate:new Date().toISOString().slice(0,10)}:x));
  const markAllPaid = ids => { const d=new Date().toISOString().slice(0,10); setReceipts(prev => prev.map(x=>ids.includes(x.id)?{...x,status:"Paid",paidDate:d}:x)); };

  const vendorName = id => vendors.find(v=>v.id===id)?.name || "Unknown";

  const Tab = ({id,label}) => (
    <button onClick={()=>{setTab(id);setSelectedVendor(null);}} style={{padding:"7px 16px",borderRadius:7,fontSize:13,fontWeight:tab===id?600:400,cursor:"pointer",border:"none",background:tab===id?D.accent:"transparent",color:tab===id?"#fff":D.muted}}>
      {label}
    </button>
  );

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div style={st.h1}>Creditors</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {tab==="monthly" && <button style={st.btn("primary")} onClick={()=>openAddReceipt()}>+ Add Receipt</button>}
          {tab==="vendors" && <button style={st.btn("primary")} onClick={openAddVendor}>+ Add Vendor</button>}
        </div>
      </div>

      <StatGrid stats={[
        {label:`${monthNames[viewMonth]} charges`, value:fmt(monthTotal), color:D.warning},
        {label:"Unpaid", value:fmt(unpaidTotal), color:D.danger},
        {label:"Paid", value:fmt(paidTotal), color:D.success},
        {label:"Active vendors", value:vendors.length, color:D.accent},
      ]}/>

      <div style={{display:"flex",gap:4,marginBottom:20,background:D.surface,padding:4,borderRadius:10,width:"fit-content",border:`1px solid ${D.border}`}}>
        <Tab id="monthly" label="Monthly View"/>
        <Tab id="vendors" label="Vendors"/>
        <Tab id="history" label="All History"/>
      </div>

      {tab==="monthly" && <>
        <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:20}}>
          <button style={st.btn()} onClick={prevMonth}>‹ Prev</button>
          <div style={{fontWeight:600,fontSize:16,minWidth:160,textAlign:"center"}}>{monthNames[viewMonth]} {viewYear}</div>
          <button style={st.btn()} onClick={nextMonth}>Next ›</button>
          {unpaidTotal>0 && <button style={{...st.btn("success"),marginLeft:"auto"}} onClick={()=>markAllPaid(monthReceipts.filter(r=>r.status==="Unpaid").map(r=>r.id))}>✓ Pay All Unpaid — {fmt(unpaidTotal)}</button>}
        </div>

        {vendors.length===0 ? (
          <div style={{...st.card,textAlign:"center",padding:"40px 20px",color:D.muted,marginBottom:20}}>
            <div style={{fontSize:28,marginBottom:8}}>🏪</div>
            <div style={{fontWeight:500,marginBottom:6}}>No vendors yet</div>
            <div style={{fontSize:13,marginBottom:16}}>Add supply houses, fuel accounts, hardware stores, or credit cards.</div>
            <button style={{...st.btn("primary"),marginLeft:0}} onClick={openAddVendor}>+ Add First Vendor</button>
          </div>
        ) : (
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:12,marginBottom:20}}>
            {vendors.map(v => {
              const total = vendorTotals[v.id]||0;
              const vReceipts = monthReceipts.filter(r=>r.vendorId===v.id);
              const unpaid = vReceipts.filter(r=>r.status==="Unpaid").reduce((s,r)=>s+(Number(r.amount)||0),0);
              const isCC = v.category==="Credit Card";
              return (
                <div key={v.id} style={{...st.stat,cursor:"pointer",border:`1px solid ${total>0?(isCC?D.accent:D.warning)+"66":D.border}`,position:"relative"}} onClick={()=>openAddReceipt(v.id)}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                    <span style={{fontSize:14}}>{isCC?"💳":"🏪"}</span>
                    <div style={{fontSize:12,fontWeight:500,color:D.text,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v.name}</div>
                  </div>
                  <div style={{fontSize:10,color:D.muted,marginBottom:8}}>{v.category}{v.ccLastFour?" ····"+v.ccLastFour:""}</div>
                  <div style={{fontSize:20,fontWeight:700,color:total>0?(isCC?D.accent:D.warning):D.muted}}>{fmt(total)}</div>
                  {unpaid>0 && <div style={{fontSize:11,color:D.danger,marginTop:4}}>Unpaid: {fmt(unpaid)}</div>}
                  {total===0 && <div style={{fontSize:11,color:D.muted,marginTop:4}}>No charges this month</div>}
                  <div style={{fontSize:10,color:D.accent,marginTop:6}}>+ Add charge</div>
                </div>
              );
            })}
          </div>
        )}

        {monthReceipts.length===0 ? (
          <div style={{...st.card,textAlign:"center",padding:"32px 20px",color:D.muted}}>
            <div style={{fontSize:28,marginBottom:8}}>🧾</div>
            <div style={{fontWeight:500,marginBottom:4}}>No receipts for {monthNames[viewMonth]}</div>
            <div style={{fontSize:13}}>Click a vendor card above or "+ Add Receipt" to log charges.</div>
          </div>
        ) : (
          <div style={st.card}>
            <div style={{fontWeight:500,fontSize:15,marginBottom:14}}>All receipts — {monthNames[viewMonth]} {viewYear}</div>
            <div style={{overflowX:"auto"}}><table style={st.table}>
              <thead><tr>{["Date","Vendor","Description","Invoice #","Amount","Status",""].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
              <tbody>
                {monthReceipts.slice().sort((a,b)=>b.date.localeCompare(a.date)).map(r => (
                  <tr key={r.id}>
                    <td style={st.td}>{fmtDate(r.date)}</td>
                    <td style={{...st.td,fontWeight:500}}>{vendorName(r.vendorId)}</td>
                    <td style={{...st.td,color:D.muted,fontSize:12,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.description||"—"}</td>
                    <td style={{...st.td,color:D.muted,fontSize:12}}>{r.invoiceNum||"—"}</td>
                    <td style={{...st.td,color:D.warning,fontWeight:600}}>{fmt(Number(r.amount)||0)}</td>
                    <td style={st.td}><span style={{background:r.status==="Paid"?"#E1F5EE":"#FCEBEB",color:r.status==="Paid"?D.success:D.danger,fontSize:11,fontWeight:500,padding:"2px 8px",borderRadius:6,border:`0.5px solid ${r.status==="Paid"?D.success:D.danger}`}}>{r.status}</span></td>
                    <td style={st.td}>
                      {r.status==="Unpaid" && <button style={{...st.btn("success"),padding:"4px 8px",fontSize:11,marginLeft:0}} onClick={()=>markReceiptPaid(r.id)}>✓ Paid</button>}
                      <button style={{...st.btn(),padding:"4px 8px",fontSize:11}} onClick={()=>openEditReceipt(r)}>Edit</button>
                      <button style={{...st.btn("danger"),padding:"4px 8px",fontSize:11}} onClick={()=>deleteReceipt(r.id)}>Del</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        )}
      </>}

      {tab==="vendors" && (
        vendors.length===0 ? (
          <div style={{...st.card,textAlign:"center",padding:"40px 20px",color:D.muted}}>
            <div style={{fontSize:32,marginBottom:12}}>🏪</div>
            <div style={{fontWeight:500,marginBottom:6}}>No vendors yet</div>
            <div style={{fontSize:13,marginBottom:16}}>Add supply houses, fuel accounts, hardware stores, or credit cards from the dropdown.</div>
            <button style={{...st.btn("primary"),marginLeft:0}} onClick={openAddVendor}>+ Add First Vendor</button>
          </div>
        ) : (
          <div style={st.card}>
            <div style={{overflowX:"auto"}}><table style={st.table}>
              <thead><tr>{["","Vendor","Category","Account #","CC Last 4","Due Day","Phone","Notes","All-time",""].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
              <tbody>
                {vendors.map(v => {
                  const total = receipts.filter(r=>r.vendorId===v.id).reduce((s,r)=>s+(Number(r.amount)||0),0);
                  const isCC = v.category==="Credit Card";
                  return (
                    <tr key={v.id}>
                      <td style={st.td}><span style={{fontSize:16}}>{isCC?"💳":"🏪"}</span></td>
                      <td style={{...st.td,fontWeight:500,color:isCC?D.accent:D.text}}>{v.name}</td>
                      <td style={st.td}><span style={{fontSize:11,background:D.surface,padding:"2px 7px",borderRadius:5,color:D.accent}}>{v.category}</span></td>
                      <td style={{...st.td,color:D.muted,fontSize:12}}>{v.accountNum||"—"}</td>
                      <td style={{...st.td,color:D.muted,fontSize:12}}>{isCC&&v.ccLastFour?"····"+v.ccLastFour:"—"}</td>
                      <td style={{...st.td,color:D.muted,fontSize:12}}>{isCC&&v.ccDueDay?"Day "+v.ccDueDay:"—"}</td>
                      <td style={{...st.td,color:D.muted,fontSize:12}}>{v.phone||"—"}</td>
                      <td style={{...st.td,color:D.muted,fontSize:12,maxWidth:140,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v.notes||"—"}</td>
                      <td style={{...st.td,color:isCC?D.accent:D.warning,fontWeight:500}}>{fmt(total)}</td>
                      <td style={st.td}>
                        <button style={{...st.btn("primary"),padding:"4px 8px",fontSize:11,marginLeft:0}} onClick={()=>openAddReceipt(v.id)}>+ Charge</button>
                        <button style={{...st.btn(),padding:"4px 8px",fontSize:11}} onClick={()=>openEditVendor(v)}>Edit</button>
                        <button style={{...st.btn("danger"),padding:"4px 8px",fontSize:11}} onClick={()=>deleteVendor(v.id)}>Del</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          </div>
        )
      )}

      {tab==="history" && <>
        <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
          <button style={{...st.btn(),marginLeft:0,background:!selectedVendor?D.accent:D.border,color:!selectedVendor?"#fff":D.text}} onClick={()=>setSelectedVendor(null)}>All vendors</button>
          {vendors.map(v=><button key={v.id} style={{...st.btn(),marginLeft:0,background:selectedVendor===v.id?D.accent:D.border,color:selectedVendor===v.id?"#fff":D.text}} onClick={()=>setSelectedVendor(v.id)}>{v.name}</button>)}
        </div>
        {allReceipts.length===0 ? (
          <div style={{...st.card,textAlign:"center",padding:"32px 20px",color:D.muted}}>No receipts found.</div>
        ) : (
          <div style={st.card}>
            <div style={{overflowX:"auto"}}><table style={st.table}>
              <thead><tr>{["Date","Vendor","Description","Invoice #","Amount","Status","Paid On",""].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
              <tbody>
                {allReceipts.slice().sort((a,b)=>b.date.localeCompare(a.date)).map(r => (
                  <tr key={r.id}>
                    <td style={st.td}>{fmtDate(r.date)}</td>
                    <td style={{...st.td,fontWeight:500}}>{vendorName(r.vendorId)}</td>
                    <td style={{...st.td,color:D.muted,fontSize:12}}>{r.description||"—"}</td>
                    <td style={{...st.td,color:D.muted,fontSize:12}}>{r.invoiceNum||"—"}</td>
                    <td style={{...st.td,color:D.warning,fontWeight:600}}>{fmt(Number(r.amount)||0)}</td>
                    <td style={st.td}><span style={{background:r.status==="Paid"?"#E1F5EE":"#FCEBEB",color:r.status==="Paid"?D.success:D.danger,fontSize:11,fontWeight:500,padding:"2px 8px",borderRadius:6,border:`0.5px solid ${r.status==="Paid"?D.success:D.danger}`}}>{r.status}</span></td>
                    <td style={{...st.td,color:D.muted,fontSize:12}}>{r.paidDate?fmtDate(r.paidDate):"—"}</td>
                    <td style={st.td}>
                      {r.status==="Unpaid" && <button style={{...st.btn("success"),padding:"4px 8px",fontSize:11,marginLeft:0}} onClick={()=>markReceiptPaid(r.id)}>✓ Paid</button>}
                      <button style={{...st.btn(),padding:"4px 8px",fontSize:11}} onClick={()=>openEditReceipt(r)}>Edit</button>
                      <button style={{...st.btn("danger"),padding:"4px 8px",fontSize:11}} onClick={()=>deleteReceipt(r.id)}>Del</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        )}
      </>}

      {vendorModal && <Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(520px,95vw)",maxHeight:"90vh",overflowY:"auto"}}>
        <MHdr title={vendorModal==="add"?"Add Vendor / Account":"Edit Vendor"} onClose={()=>setVendorModal(null)}/>

        {vendorModal==="add" && <>
          <Field label="Vendor type">
            <select style={st.input} value={vData.presetGroup||"Supply Houses"} onChange={e=>setVD("presetGroup",e.target.value)}>
              {Object.keys(PRESET_VENDORS).map(g=><option key={g}>{g}</option>)}
            </select>
          </Field>
          <Field label="Select vendor">
            <select style={st.input} value={vData.presetName||""} onChange={e=>{
              const name = e.target.value;
              setVD("presetName", name);
              if (name && name!=="Custom / Other") {
                setVD("name", name);
                const cat = vData.presetGroup==="Credit Cards" ? "Credit Card" : (PRESET_CATS[vData.presetGroup]||"Other");
                setVD("category", cat);
                setVD("isCC", vData.presetGroup==="Credit Cards");
              } else {
                setVD("name","");
              }
            }}>
              <option value="">— Select —</option>
              {(PRESET_VENDORS[vData.presetGroup||"Supply Houses"]||[]).map(n=><option key={n}>{n}</option>)}
            </select>
          </Field>
          {(vData.presetName==="Custom / Other" || !vData.presetName) && <Field label="Vendor name"><input style={st.input} value={vData.name||""} onChange={e=>setVD("name",e.target.value)} placeholder="Enter name" autoFocus/></Field>}
          <div style={{borderTop:`1px solid ${D.border}`,marginBottom:16}}/>
        </>}

        {vendorModal==="edit" && <div style={{marginBottom:14}}><Field label="Vendor Name"><input style={st.input} value={vData.name||""} onChange={e=>setVD("name",e.target.value)}/></Field></div>}

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <Field label="Category">
            <select style={st.input} value={vData.category||"Supply House"} onChange={e=>{setVD("category",e.target.value);setVD("isCC",e.target.value==="Credit Card");}}>
              {CREDITOR_CATS.map(c=><option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Account # (optional)"><input style={st.input} value={vData.accountNum||""} onChange={e=>setVD("accountNum",e.target.value)} placeholder="ACCT-12345"/></Field>

          {(vData.category==="Credit Card"||vData.isCC) && <>
            <Field label="Last 4 digits"><input style={st.input} type="number" maxLength={4} value={vData.ccLastFour||""} onChange={e=>setVD("ccLastFour",e.target.value.slice(0,4))} placeholder="1234"/></Field>
            <Field label="Statement closing day"><input style={st.input} type="number" min="1" max="31" value={vData.ccDueDay||""} onChange={e=>setVD("ccDueDay",e.target.value)} placeholder="15"/></Field>
            <div style={{gridColumn:"1/-1",padding:"10px 14px",background:D.accent+"11",borderRadius:8,border:`1px solid ${D.accent}33`,fontSize:12,color:D.muted}}>
              💳 Credit card charges will be tracked separately and shown with a card icon. Enter monthly statements as single charges or itemize individual purchases.
            </div>
          </>}

          <Field label="Phone (optional)"><input style={st.input} value={vData.phone||""} onChange={e=>setVD("phone",e.target.value)} placeholder="239-555-0100"/></Field>
          <div style={{gridColumn:"1/-1"}}><Field label="Notes"><input style={st.input} value={vData.notes||""} onChange={e=>setVD("notes",e.target.value)} placeholder="Net 30, pickup only, card due 15th, etc."/></Field></div>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",marginTop:20}}>
          <button style={st.btn()} onClick={()=>setVendorModal(null)}>Cancel</button>
          <button style={st.btn("primary")} onClick={saveVendor} disabled={!vData.name&&!vData.presetName}>Save</button>
        </div>
      </div></Overlay>}

      {receiptModal && (() => {
        const v = vendors.find(x=>x.id===rData.vendorId);
        const isCC = v?.category==="Credit Card";
        return (
          <Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(520px,95vw)",maxHeight:"90vh",overflowY:"auto"}}>
            <MHdr title={receiptModal==="add"?(isCC?"Add CC Charge":"Add Receipt / Charge"):(isCC?"Edit CC Charge":"Edit Receipt")} onClose={()=>setReceiptModal(null)}/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              <div style={{gridColumn:"1/-1"}}><Field label={isCC?"Credit Card":"Vendor"}>
                <select style={st.input} value={rData.vendorId||""} onChange={e=>setRD("vendorId",parseInt(e.target.value))}>
                  {vendors.map(v=><option key={v.id} value={v.id}>{v.category==="Credit Card"?"💳 ":"🏪 "}{v.name}</option>)}
                </select>
              </Field></div>
              <Field label="Date"><input style={st.input} type="date" value={rData.date||""} onChange={e=>setRD("date",e.target.value)}/></Field>
              <Field label={isCC?"Charge Amount ($)":"Amount ($)"}><input style={st.input} type="number" min="0" step="0.01" value={rData.amount||""} onChange={e=>setRD("amount",e.target.value)} placeholder="0.00" autoFocus/></Field>
              <Field label={isCC?"CC Statement / Ref #":"Vendor Invoice / Receipt #"}><input style={st.input} value={rData.invoiceNum||""} onChange={e=>setRD("invoiceNum",e.target.value)} placeholder={isCC?"STMT-2026-05":"INV-98765"}/></Field>
              <Field label="Status"><select style={st.input} value={rData.status||"Unpaid"} onChange={e=>setRD("status",e.target.value)}><option>Unpaid</option><option>Paid</option></select></Field>
              {rData.status==="Paid" && <Field label="Paid Date"><input style={st.input} type="date" value={rData.paidDate||new Date().toISOString().slice(0,10)} onChange={e=>setRD("paidDate",e.target.value)}/></Field>}
              <div style={{gridColumn:"1/-1"}}><Field label="Description"><input style={st.input} value={rData.description||""} onChange={e=>setRD("description",e.target.value)} placeholder={isCC?"Home Depot card — May statement":"Wire, conduit, breakers — panel job"}/></Field></div>
              <div style={{gridColumn:"1/-1"}}><Field label="Notes"><input style={st.input} value={rData.notes||""} onChange={e=>setRD("notes",e.target.value)} placeholder="Optional"/></Field></div>
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",marginTop:20}}>
              <button style={st.btn()} onClick={()=>setReceiptModal(null)}>Cancel</button>
              <button style={st.btn("primary")} onClick={saveReceipt}>Save</button>
            </div>
          </div></Overlay>
        );
      })()}
    </div>
  );
}
